<template>
  <div class="report-display">
    <div class="report-header">
      <div class="report-title-block">
        <div class="report-title">{{ fileName || 'Time Report' }}</div>
        <div class="report-meta">
          {{ combinedTimeEntries.length }} entries &nbsp;·&nbsp; Total: <strong>{{ totalHoursDisplay }} hours</strong>
      </div>
      <Button variant="smooth primary" class="mt-4" @click="downloadPdf">
        Download PDF <i class="icofont-download ml-2"></i>
      </Button>
        </div>
    </div>

    <div class="table-wrap">
      <table ref="table">
        <thead>
          <tr>
            <th class="col-date">Date</th>
            <th class="col-project">Project</th>
            <th class="col-hours">Hours</th>
            <th class="col-desc">Description</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(timeEntry, i) in combinedTimeEntries" :key="i">
            <td class="col-date">{{ timeEntry.date }}</td>
            <td class="col-project">
              <span class="project-badge">{{ getProjectName(timeEntry.projectId) }}</span>
            </td>
            <td class="col-hours">{{ hoursDisplay(timeEntry.totalTime) }}</td>
            <td class="col-desc">{{ timeEntry.description }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="2" class="total-label">Total</td>
            <td class="col-hours total-value">{{ totalHoursDisplay }}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
  import {
    getFirstLineFromHtml,
    durationAsHours,
    formatDate
  } from '@/utils'
  import Button from '../common/Button.vue'
  import { jsPDF } from 'jspdf'

  export default {
    name: 'ReportDisplay',
    components: { Button },
    props: ['timeEntries', 'fileName'],
    computed: {
      projects() {
        return this.$store.getters.projects
      },
      combinedTimeEntries() {
        const combinedTimeEntries = []

        for (const timeEntry of this.timeEntries) {
          if (!timeEntry.end) continue

          const existingEntryIndex = combinedTimeEntries.findIndex((entry) => {
            return (
              this.formatDate(timeEntry.start) === entry.date &&
              timeEntry.projectId === entry.projectId &&
              this.getFirstLineFromHtml(timeEntry.description) === entry.description
            )
          })

          if (existingEntryIndex > -1) {
            combinedTimeEntries[existingEntryIndex].totalTime +=
              durationAsHours(timeEntry.start, timeEntry.end)
          } else {
            combinedTimeEntries.push({
              date: this.formatDate(timeEntry.start),
              projectId: timeEntry.projectId,
              description: this.getFirstLineFromHtml(timeEntry.description),
              totalTime: durationAsHours(timeEntry.start, timeEntry.end)
            })
          }
        }
        return combinedTimeEntries
      },
      totalHoursDisplay() {
        const total = this.combinedTimeEntries.reduce((sum, e) => sum + e.totalTime, 0)
        return this.hoursDisplay(total)
      }
    },
    methods: {
      getProjectName(projectId) {
        return this.projects.find((p) => p.id === projectId)?.title || ""
      },
      hoursDisplay(hours) {
        return (Math.round(hours * 100) / 100 + '').replaceAll('.', ',')
      },
      async downloadPdf() {
        const pdf = new jsPDF({ unit: 'px' })

        let rowsHtml = ''
        let totalTime = 0

        for (const entry of this.combinedTimeEntries) {
          totalTime += entry.totalTime
          rowsHtml += `
            <tr style="border-bottom: 1px solid #E2E8F0;">
              <td style="padding: 5px 8px; white-space: nowrap;">${entry.date}</td>
              <td style="padding: 5px 8px; max-width: 80px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${this.getProjectName(entry.projectId)}</td>
              <td style="padding: 5px 8px; white-space: nowrap;">${this.hoursDisplay(entry.totalTime)}</td>
              <td style="padding: 5px 8px; max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${entry.description || ''}</td>
            </tr>
          `
        }

        await pdf.html(`
          <div style="font-size: 8px; width: 440px; font-family: Helvetica, sans-serif; padding: 20px; color: #2D3748;">
            <div style="font-size: 18px; font-weight: bold; margin-bottom: 4px; color: #1A202C;">${this.fileName || 'Time Report'}</div>
            <div style="font-size: 10px; color: #718096; margin-bottom: 16px;">${this.combinedTimeEntries.length} entries</div>
            <table style="width: 100%; border-collapse: collapse; border: 1px solid #E2E8F0; border-radius: 6px;">
              <thead>
                <tr style="background: #F7FAFC;">
                  <th style="text-align: left; padding: 6px 8px; font-size: 8px; color: #4A5568; border-bottom: 2px solid #E2E8F0;">Date</th>
                  <th style="text-align: left; padding: 6px 8px; font-size: 8px; color: #4A5568; border-bottom: 2px solid #E2E8F0;">Project</th>
                  <th style="text-align: left; padding: 6px 8px; font-size: 8px; color: #4A5568; border-bottom: 2px solid #E2E8F0;">Hours</th>
                  <th style="text-align: left; padding: 6px 8px; font-size: 8px; color: #4A5568; border-bottom: 2px solid #E2E8F0;">Description</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
              <tfoot>
                <tr style="background: #F7FAFC; border-top: 2px solid #E2E8F0;">
                  <td colspan="2" style="padding: 6px 8px; font-weight: bold;">Total</td>
                  <td style="padding: 6px 8px; font-weight: bold;">${this.hoursDisplay(totalTime)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        `)
        pdf.save(this.fileName)
      },
      formatDate,
      getFirstLineFromHtml,
      durationAsHours
    }
  }
</script>

<style scoped>
  .report-display {
    padding: 8px 4px;
    min-width: 560px;
  }

  .report-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    gap: 1rem;
  }

  .report-title {
    font-size: 1.15rem;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.85);
    margin-bottom: 3px;
  }

  .report-meta {
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.4);
  }

  .report-meta strong {
    color: rgba(0, 0, 0, 0.6);
    font-weight: 600;
  }

  .table-wrap {
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.08);
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
  }

  thead tr {
    background: rgba(0, 0, 0, 0.03);
  }

  th {
    text-align: left;
    padding: 10px 14px;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(0, 0, 0, 0.35);
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    white-space: nowrap;
  }

  tbody tr {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    transition: background 0.15s ease;
  }

  tbody tr:last-child {
    border-bottom: none;
  }

  tbody tr:hover {
    background: rgba(0, 0, 0, 0.03);
  }

  td {
    padding: 10px 14px;
    color: rgba(0, 0, 0, 0.75);
    vertical-align: middle;
  }

  tfoot tr {
    background: rgba(0, 0, 0, 0.03);
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }

  tfoot td {
    padding: 10px 14px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.8);
    font-size: 0.85rem;
  }

  .total-label {
    color: rgba(0, 0, 0, 0.35);
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .col-date { width: 100px; white-space: nowrap; }
  .col-project { width: 130px; }
  .col-hours { width: 70px; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .col-desc { color: rgba(0, 0, 0, 0.45); }

  .project-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 100px;
    background: var(--primary-muted);
    color: var(--primary);
    font-size: 0.78rem;
    font-weight: 500;
    white-space: nowrap;
  }
</style>
